import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Shared button primitive. Section E.1 of
 * docs/design-system/HPOS-V2-VISUAL-SYSTEM.md is the specification; the base
 * classes, six variants, and four sizes below are that section verbatim.
 *
 * Two deliberate departures from the legacy source
 * (artifacts/high-performer-os/src/components/ui/button.tsx):
 *
 * 1. The legacy `hover-elevate`, `active-elevate-2`, `--button-outline`, and
 *    `--primary-border` / `--secondary-border` / `--destructive-border`
 *    references are omitted. They are Replit-plugin artifacts and are undefined
 *    in legacy's own index.css, so they are inert there too; porting them would
 *    import dead code (PPS-002 section 9.3, prohibited pattern 8). The variants
 *    below substitute `border-border`, as the specification directs.
 *
 * 2. Legacy supports `asChild` through `@radix-ui/react-slot`. That package is
 *    not an approved dependency for this slice, so `asChild` is not
 *    implemented. No v2 call site needs it; add it with the dependency if one
 *    ever does.
 *
 * `focus-visible:ring-1 focus-visible:ring-ring` is mandatory and must not be
 * removed without an equally visible replacement (section E.7).
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md ' +
    'text-sm font-medium transition-colors focus-visible:outline-none ' +
    'focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none ' +
    'disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground border border-border',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm',
        outline: 'border border-border shadow-xs active:shadow-none',
        secondary: 'border border-border bg-secondary text-secondary-foreground',
        ghost: 'border border-transparent',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'min-h-9 px-4 py-2',
        sm: 'min-h-8 rounded-md px-3 text-xs',
        lg: 'min-h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = 'Button';
